import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { IconEmail } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'

import { useAccountEmailChangeCreate } from '@/api/gen'
import { TextInput } from '@/components/text-input'
import { getAxiosErrorMessage } from '@/utils/helpers'

export type ChangeEmailModalProps = {
  open: boolean
  currentEmail: string
  onClose: () => void
  onSuccess: (newEmail: string) => void
  isVerificationRequest?: boolean
  backUrl?: string
}

export const ChangeEmailModal = ({
  open,
  currentEmail,
  onClose,
  onSuccess,
  isVerificationRequest = false,
  backUrl,
}: ChangeEmailModalProps) => {
  const { formatMessage } = useIntl()
  const [email, setEmail] = useState(currentEmail)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const { mutate: changeEmail, isPending } = useAccountEmailChangeCreate({
    mutation: {
      onSuccess: () => {
        onSuccess(email)
        handleClose()
      },
      onError: (error: AxiosError) => {
        const errorMessage =
          getAxiosErrorMessage(error) || formatMessage({ id: 'user.settings.email.error' })
        setApiError(errorMessage)
      },
    },
  })

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setValidationError(null)
    setApiError(null)
  }

  const handleSave = () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email || !emailRegex.test(email)) {
      setValidationError(formatMessage({ id: 'common.validation.invalidEmail' }))

      return
    }

    // Call API to change email
    setApiError(null)
    changeEmail({
      data: {
        email,
        ...(isVerificationRequest && { is_verification_request: true }),
        ...(backUrl !== undefined && { back_url: backUrl }),
      },
    })
  }

  // Check if email is unchanged
  const isEmailUnchanged = email === currentEmail

  // Check if email is valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const isEmailInvalid = !email || !emailRegex.test(email)

  // Disable save button if email unchanged, invalid, or pending
  const isSaveDisabled = isPending || isEmailUnchanged || isEmailInvalid

  const handleClose = () => {
    setEmail(currentEmail)
    setValidationError(null)
    setApiError(null)
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-[500px]">
          <Dialog.Header
            className="justify-center"
            size="lg"
          >
            <FormattedMessage id="user.settings.email.changeModal.title" />
          </Dialog.Header>
          <Dialog.Content>
            <div>
              <TextInput
                error={validationError}
                label="common.labels.email"
                input={{
                  autoFocus: true,
                  disabled: isPending,
                  name: 'email',
                  placeholder: formatMessage({ id: 'user.settings.email.changeModal.placeholder' }),
                  type: 'email',
                  value: email,
                  endAddon: <IconEmail />,
                  onChange: handleEmailChange,
                }}
              />

              {apiError !== null && (
                <Alert
                  className="mt-4"
                  heading={apiError}
                  severity="warning"
                />
              )}
            </div>
          </Dialog.Content>
          <Dialog.Footer className="flex-col">
            <Button
              fullWidth
              color="secondary"
              disabled={isPending}
              size="large"
              variant="contained"
              onClick={handleClose}
            >
              <FormattedMessage id="common.controls.cancel" />
            </Button>
            <Button
              fullWidth
              color="primary"
              disabled={isSaveDisabled}
              loading={isPending}
              size="large"
              variant="contained"
              onClick={handleSave}
            >
              <FormattedMessage id="user.settings.email.changeModal.saveAndResend" />
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
